// src/context/LanguageContext.tsx
'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../i18n/en/ReactAppShell.json';
import de from '../i18n/de/ReactAppShell.json';

type Language = 'en' | 'de';
type Translations = typeof en;

// Allow both known keys from the JSON and arbitrary strings
type TranslationKey = keyof Translations | string;

interface LanguageContextType {
  language: Language;
  t: (key: TranslationKey) => string;
  switchLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    const saved = localStorage.getItem('lang') as Language | null;
    if (saved) {
      setLanguage(saved);
    }
  }, []);

  // Treat translations as a generic key/value map
  const translations: Record<string, string> =
    language === 'de'
      ? (de as unknown as Record<string, string>)
      : (en as unknown as Record<string, string>);

  const t = (key: TranslationKey): string => {
    const k = String(key);
    return translations[k] ?? k;
  };

  const switchLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, t, switchLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return ctx;
};
